import { Directive, ElementRef, HostListener, Input, OnInit, Renderer2 } from '@angular/core';
import { MatRipple } from '@angular/material/core';

interface ButtonStyleMapping {
  attribute: string;
  styleProperties: ButtonStyleProperties[];
}

interface ButtonStyleProperties {
  colorAttribute: ColorAttribute;
  classList: string[];
}

export type ColorAttribute = undefined | 'primary' | 'basic' | 'warn';

@Directive({
  standalone: true,
  selector: '[hk-button-flat], [hk-button-stroked], [hk-button-raised], [hk-button-close]',
  providers: [MatRipple],
})
export class HKButtonDirective implements OnInit {
  private static readonly attributeToClassMap: ButtonStyleMapping[] = [
    {
      attribute: 'hk-button-flat',
      styleProperties: [
        { colorAttribute: undefined, classList: ['hk-button-basic-flat'] },
        { colorAttribute: 'primary', classList: ['hk-button-primary-flat'] },
        { colorAttribute: 'basic', classList: ['hk-button-basic-flat'] },
        { colorAttribute: 'warn', classList: ['hk-button-warn-flat'] },
      ],
    },
    {
      attribute: 'hk-button-stroked',
      styleProperties: [
        { colorAttribute: undefined, classList: ['hk-button-basic-stroked'] },
        { colorAttribute: 'primary', classList: ['hk-button-primary-stroked'] },
        { colorAttribute: 'basic', classList: ['hk-button-basic-stroked'] },
        { colorAttribute: 'warn', classList: ['hk-button-warn-stroked'] },
      ],
    },
    {
      attribute: 'hk-button-raised',
      styleProperties: [
        { colorAttribute: undefined, classList: ['hk-button-primary-raised'] },
        { colorAttribute: 'primary', classList: ['hk-button-primary-raised'] },
        { colorAttribute: 'warn', classList: ['hk-button-warn-raised'] },
      ],
    },
    {
      attribute: 'hk-button-close',
      styleProperties: [{ colorAttribute: undefined, classList: ['hk-button-close'] }],
    },
  ];
  @Input() color: ColorAttribute;

  constructor(
    private elementReference: ElementRef,
    private renderer: Renderer2,
    private ripple: MatRipple,
  ) {}

  @HostListener('mousedown', ['$event']) onmousedown(event: MouseEvent) {
    this.ripple.launch(event.x, event.y);
  }

  ngOnInit(): void {
    this.applyButtonClasses();
  }

  private applyButtonClasses(): void {
    const element = this.elementReference.nativeElement;
    if (!element.getAttribute('type')) element.setAttribute('type', 'button');

    for (const mapping of HKButtonDirective.attributeToClassMap) {
      if (element.hasAttribute(mapping.attribute)) {
        this.renderer.addClass(element, 'hk-button');

        if (this.isNoLabel(this.elementReference)) {
          this.renderer.addClass(element, 'hk-button-no-label');
        }

        const styleProperties = mapping.styleProperties.find((properties) => properties.colorAttribute === this.color);
        if (styleProperties) {
          for (const className of styleProperties.classList) {
            this.renderer.addClass(element, className);
          }
        }
      }
    }
  }

  private isNoLabel(element: ElementRef) {
    if (element.nativeElement.childNodes.length === 1) {
      const child = element.nativeElement.childNodes[0];
      return child.nodeType === Node.ELEMENT_NODE && child.tagName.toLowerCase() === 'i';
    }
    return false;
  }
}
